import React from "react";
import clsx from 'clsx'
import {Button, Checkbox, LoadingButton, StackIcon} from 'shared/ui'
import {WEBHOOK_EVENTS} from '../constants'
import {generateSecretToken} from '../hooks/useSecretTokenGenerator'
import formCss from '../../CreateWebhookModal/CreateWebhookModal.module.scss'
import type {UseWebhookFormReturn} from '../hooks/useWebhookForm'

interface WebhookFormProps {
	form: UseWebhookFormReturn
	isSaving: boolean
	isEditing: boolean
	onSubmit: (payload: {
		uri: string
		api_key?: string
		events: string[]
	}) => void
	onCancel: () => void
}

export function WebhookForm({
	form,
	isSaving,
	isEditing,
	onSubmit,
	onCancel
}: WebhookFormProps) {
	const handleGenerateApiKey = () => {
		const token = generateSecretToken()
		form.setApiKey(token)
		form.setApiKeyErrorMessage(null)
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		const {urlErr} = form.validateAndGetErrors()

		if (urlErr) {
			return
		}

		onSubmit({
			uri: form.url.trim(),
			api_key: form.apiKey.trim() || undefined,
			events: form.selectedEvents || []
		})
	}

	return (
		<form className={formCss.form} noValidate onSubmit={handleSubmit}>
			<div className={clsx(formCss.field, formCss.urlField)}>
				<label className={formCss.label} htmlFor="url">
					URL
				</label>
				<input
					autoComplete="off"
					className={clsx(
						formCss.input,
						form.urlErrorMessage && formCss.inputError
					)}
					id="url"
					onChange={e => form.setUrl(e.target.value)}
					placeholder="https://example.com/webhook"
					type="text"
					value={form.url}
				/>
				<span
					className={clsx(
						formCss.fieldErrorText,
						!form.urlErrorMessage && formCss.fieldErrorTextHidden
					)}
				>
					{form.urlErrorMessage || '\u00A0'}
				</span>
			</div>

			<div className={formCss.field}>
				<label className={formCss.label} htmlFor="apiKey">
					Secret Token
				</label>
				<div className={formCss.apiKeyContainer}>
					<input
						autoComplete="off"
						className={clsx(
							formCss.input,
							form.apiKeyErrorMessage && formCss.inputError
						)}
						id="apiKey"
						onChange={e => form.setApiKey(e.target.value)}
						placeholder="Enter a secret token (optional)"
						type="text"
						value={form.apiKey}
					/>
					<button
						className={formCss.refreshButton}
						onClick={handleGenerateApiKey}
						type="button"
						title="Generate secret token"
					>
						<StackIcon icon="refresh" width={24} height={24}/>
					</button>
				</div>
				<span
					className={clsx(
						formCss.fieldErrorText,
						!form.apiKeyErrorMessage && formCss.fieldErrorTextHidden
					)}
				>
					{form.apiKeyErrorMessage || '\u00A0'}
				</span>
				<p className={formCss.apiKeyHint}>
					Used to validate received payloads. Sent with the request in the{' '}
					<code className={formCss.inlineCode}>X-Snaily-Token</code> HTTP header.
				</p>
			</div>

			<div className={formCss.field}>
				<label className={formCss.label}>Triggers</label>
				<div className={formCss.eventsList}>
					{WEBHOOK_EVENTS.map(event => {
						const isSelected = form.selectedEvents.includes(event.id)

						return (
							<div key={event.id} className={formCss.eventItem}>
								<div className={formCss.eventLabel}>
									<Checkbox
										checked={isSelected}
										onChange={() => form.toggleEvent(event.id)}
									/>
									<span className={formCss.eventName}>{event.name}</span>
									<code className={formCss.eventCode}>{event.id}</code>
								</div>
								<p className={formCss.eventDescription}>
									{event.description}
								</p>
							</div>
						)
					})}
				</div>
			</div>

			<div className={formCss.actions}>
				<Button
					className={formCss.cancelButton}
					onClick={onCancel}
					type="button"
					variant="bordered"
				>
					Cancel
				</Button>
				<LoadingButton
					className={formCss.submitButton}
					disabled={isSaving}
					isLoading={isSaving}
					type="submit"
				>
					{isEditing ? 'Save webhook' : 'Add Webhook'}
				</LoadingButton>
			</div>
		</form>
	)
}